import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Avatar,
  Checkbox,
  Chip,
  Button,
} from '@mui/material';
import {
  Edit,
  Delete,
  Star,
  Schedule,
} from '@mui/icons-material';
import { contactAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import useDebounce from '../../utils/useDebounce';
import {
  PageHeader,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tableAvatarStyles,
  standardTextStyles,
  emptyStateTextStyles,
  emptyStateLinkStyles,
} from '../../components/common';

const ContactList = () => {
  const navigate = useNavigate();
  const { canDelete } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(80);
  const [total, setTotal] = useState(1869); // Default to match image
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getAll({
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
      });
      setContacts(response.data.contacts);
      setTotal(response.data.pagination.totalItems || 1869);
    } catch (error) {
      toast.error('Failed to load contacts');
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, contact) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedContact(contact);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContact(null);
  };

  const handleDelete = async () => {
    if (!selectedContact) return;

    if (window.confirm(`Are you sure you want to delete ${selectedContact.name}?`)) {
      try {
        await contactAPI.delete(selectedContact._id);
        toast.success('Contact deleted successfully');
        fetchContacts();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete contact');
      }
    }
    handleMenuClose();
  };

  const handleToggleStar = async (e, contact) => {
    e.stopPropagation();
    try {
      await contactAPI.toggleStar(contact._id);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedContacts(contacts.map((c) => c._id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (event, contactId) => {
    event.stopPropagation();
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter((id) => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, total);

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Page Header */}
      <PageHeader
        title="Contacts"
        newButtonText="New"
        onNew={() => navigate('/contacts/new')}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search contacts..."
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        viewModes={['list', 'kanban']}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(newPage) => setPage(newPage + 1)}
      />

      {/* Table */}
      <Box sx={{ backgroundColor: 'white' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowStyles}>
                <TableCell padding="checkbox" sx={{ width: 40 }}>
                  <Checkbox
                    size="small"
                    checked={
                      selectedContacts.length === contacts.length && contacts.length > 0
                    }
                    indeterminate={
                      selectedContacts.length > 0 &&
                      selectedContacts.length < contacts.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  Activities
                </TableCell>
                <TableCell>Country</TableCell>
                <TableCell align="right" sx={{ width: 100 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={emptyStateTextStyles}>
                      No contacts found.{' '}
                      <Typography
                        component="span"
                        sx={{ ...emptyStateLinkStyles, cursor: 'pointer' }}
                        onClick={() => navigate('/contacts/new')}
                      >
                        Add your first contact
                      </Typography>
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow
                    key={contact._id}
                    hover
                    sx={tableBodyRowStyles}
                    onClick={() => navigate(`/contacts/${contact._id}/edit`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedContacts.includes(contact._id)}
                        onChange={(e) => handleSelectContact(e, contact._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ ...tableAvatarStyles, bgcolor: contact.avatarColor || '#875A7B' }}>
                          {contact.name?.charAt(0)?.toUpperCase() || 'C'}
                        </Avatar>
                        <Typography sx={standardTextStyles}>
                          {contact.name}
                          {contact.company && contact.contactType === 'individual' && (
                            <Typography
                              component="span"
                              sx={{ color: '#666', fontSize: '13px' }}
                            >
                              , {contact.company}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={standardTextStyles}>
                        {contact.email || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={standardTextStyles}>
                        {contact.phone || contact.mobile || ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" sx={{ color: '#ccc' }}>
                        <Schedule sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography sx={standardTextStyles}>
                        {contact.country || ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                        }}
                      >
                        {contact.starred && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleToggleStar(e, contact)}
                            sx={{ color: '#f0ad4e', p: 0.25 }}
                          >
                            <Star sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        {contact.totalAmount > 0 && (
                          <Chip
                            label={`$${contact.totalAmount}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              bgcolor: '#5bc0de',
                              color: 'white',
                              fontWeight: 500,
                              borderRadius: '4px',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/contacts/${selectedContact?._id}/edit`);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" /> Edit
        </MenuItem>
        {canDelete && canDelete() && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} fontSize="small" /> Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ContactList;
